import React, { useState, useEffect } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend
} from 'chart.js';
import LoadingSpinner from './LoadingSpinner';
import Button from './ui/Button';
import StateBlock from './ui/StateBlock';
import { showError } from '../utils/toast';
import API_URL from '../config';
import MarkdownText from './ReportPrintable/MarkdownText';
import { formatHipoteseAlternativaPorque } from '../utils/porqueFormat';
import { ramo6MTemConteudo } from '../utils/ishikawa';
import { formatCurrencyBR } from '../utils/currency';
import { formatDateTime } from '../utils/formatDateTime';
import { isPlaceholderFisico, labelsFisicos } from '../utils/setor';
import { labelDisposicao, labelSimNao } from '../utils/iso87'; // #279/#280
import { formatCNPJ } from '../utils/masks'; // #314 — CNPJ do tenant no relatório
import './ReportV2.css';

ChartJS.register(ArcElement, Tooltip, Legend);

// Helper: fetch com token JWT automático
function authFetch(url, options = {}) {
  const token = localStorage.getItem('token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
}

// Configuração das seções selecionáveis para exportação PDF
const PDF_SECTIONS = [
  { key: 'identificacao',      label: 'Identificação (cabeçalho)' },
  { key: 'cliente_emissor',    label: 'Cliente emissor (origem externa)' },
  { key: 'descricao',          label: 'Descrição da NC' },
  { key: 'custo',              label: 'Custo estimado (R$)' },
  { key: 'score',              label: 'Completude da análise' },
  { key: 'porques',            label: 'Análise dos porquês' },
  { key: 'causa_raiz',         label: 'Causa Raiz' },
  { key: 'analise_6m',         label: 'Análise 6M (Ishikawa)' },
  { key: 'hipotese_alternativa', label: 'Hipótese Alternativa' },
  { key: 'acao_imediata',      label: 'Ação Imediata' },
  { key: 'contencao',          label: 'Ação de Contenção' },
  { key: 'acoes',              label: 'Ações Corretivas' },
  { key: 'acoes_preventivas',  label: 'Ações Preventivas' },
  { key: 'impacto',            label: 'Impacto Potencial' },
  { key: 'risco',              label: 'Risco de Recorrência' },
  { key: 'conclusao',          label: 'Conclusão Executiva' },
  { key: 'plano_5w2h',         label: 'Plano 5W2H' },
  { key: 'verificacao_eficacia', label: 'Verificação de Eficácia' },
  { key: 'gantt',              label: 'Cronograma (Gantt)' },
  { key: 'validacao',          label: 'Validação e Status' },
  { key: 'anexos',             label: 'Evidências Anexadas' },
];

// #163 — seções que NÃO existem no modo Registro (schema enxuto SCHEMA_REGISTRO
// + corte de tier #210 + "Registro não recebe nota"): no Registro o seletor de
// PDF não deve oferecê-las. Registro TEM: identificação, descrição, custo,
// porquês, causa raiz, contenção, ações corretivas, conclusão, validação,
// anexos. NÃO tem as abaixo (inclui o score/Completude — Registro não recebe nota).
const SECOES_INVESTIGACAO_ONLY = new Set([
  'score', 'analise_6m', 'hipotese_alternativa', 'acoes_preventivas',
  'impacto', 'risco', 'verificacao_eficacia', 'plano_5w2h', 'gantt',
]);
const secoesPdfPorModo = (modo) =>
  PDF_SECTIONS.filter((s) => modo !== 'registro' || !SECOES_INVESTIGACAO_ONLY.has(s.key));

function ReportV2({ ncId, onClose }) {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPdfPanel, setShowPdfPanel] = useState(false);
  const [pdfSelection, setPdfSelection] = useState(
    PDF_SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: true }), {})
  );

  const togglePdfSection = (key) =>
    setPdfSelection((prev) => ({ ...prev, [key]: !prev[key] }));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchReportData(); }, [ncId]);

  const fetchReportData = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/v2/nc/${ncId}/relatorio-completo`);
      if (!response.ok) throw new Error('Erro ao carregar relatório');
      const data = await response.json();
      setReportData(data);
    } catch (error) {
      console.error('Erro ao buscar relatório:', error);
      showError('Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  // NOVO fluxo de PDF (texto selecionável):
  // abre rota standalone /relatorio-pdf/<id>?sections=... que dispara
  // window.print() automaticamente. Usuário escolhe "Salvar como PDF"
  // no diálogo do browser → PDF com texto vetorial nativo (Ctrl+F funciona,
  // copy/paste funciona, screen readers funcionam).
  const exportToPDF = () => {
    const disponiveis = secoesPdfPorModo(reportData?.analise_v4?.modo_analise);
    const enabledKeys = disponiveis
      .filter((s) => pdfSelection[s.key])
      .map((s) => s.key);
    const sectionsParam = enabledKeys.length > 0 && enabledKeys.length < disponiveis.length
      ? `?sections=${enabledKeys.join(',')}`
      : '';
    const url = `/relatorio-pdf/${ncId}${sectionsParam}`;
    window.open(url, '_blank');
  };

  if (loading) return <LoadingSpinner overlay message="Carregando relatório..." />;

  if (!reportData) {
    return (
      <div className="rv2-overlay">
        <div className="rv2-error">
          <StateBlock
            kind="error"
            message="Erro ao carregar relatório"
            action={<Button variant="secondary" onClick={onClose}>Fechar</Button>}
          />
        </div>
      </div>
    );
  }

  const { dados_basicos, analise, recorrencia, graficos } = reportData;
  const empresa = reportData.empresa || {};
  const score = graficos?.score_qualidade?.score || 0;
  const scoreColor = graficos?.score_qualidade?.color || '#006948';
  // #163 — seletor de PDF só oferece as seções que existem no modo da NC.
  const secoesPdf = secoesPdfPorModo(reportData.analise_v4?.modo_analise);

  // Logo do cliente: usa data URL (base64) se disponível, senão monta URL do servidor
  const clientLogoSrc = empresa.logo_url
    ? (empresa.logo_url.startsWith('data:') ? empresa.logo_url : `${API_URL}/${empresa.logo_url}`)
    : null;
  const empresaNome = empresa.nome_fantasia || empresa.razao_social || 'Qaa.sa';
  const ncNumero = dados_basicos?.numero_nc || `NC-${ncId}`;

  const scoreChartData = {
    labels: ['Score', 'Restante'],
    datasets: [{ data: [score, 100 - score], backgroundColor: [scoreColor, '#e7eeff'], borderWidth: 0 }]
  };
  const scoreChartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    cutout: '75%'
  };

  const statusLabel = {
    // v3.4.0 — status oficiais
    rascunho:             'Rascunho',
    aberta:               'Aberta',
    em_analise:           'Em Análise',
    aguardando_aprovacao: 'Aguard. Aprovação',
    aprovada:             'Aprovada',
    encerrada:            'Encerrada',
    rejeitada:            'Rejeitada',
    // legado v2.0
    em_revisao:           'Em Revisão',
    aprovado:             'Aprovado',
    reprovado:            'Reprovado',
  };

  return (
    <div className="rv2-overlay">
      <div className="rv2-modal">

        {/* Header — sempre com logo Qaa.sa (versão escura para fundo claro) */}
        <div className="rv2-header">
          <div className="rv2-header-left">
            <img src="/qaasa-texto-dark.png" alt="Qaa.sa" className="rv2-header-logo" />
            <div>
              <span className="rv2-header-super">Quality as a service, powered by AI</span>
              <h1 className="rv2-header-title">Relatório de {ncNumero}</h1>
            </div>
          </div>
          <div className="rv2-header-actions">
            <Button variant="secondary" onClick={onClose}>Fechar</Button>
            <Button
              variant="secondary"
              icon="tune"
              onClick={() => setShowPdfPanel(v => !v)}
              title="Selecionar seções a incluir no PDF"
            >
              Seções PDF
            </Button>
            <Button variant="primary" icon="picture_as_pdf" onClick={exportToPDF}>
              Exportar PDF
            </Button>
          </div>
        </div>

        {/* Painel de seleção de seções a exportar (D2) */}
        {showPdfPanel && (
          <div className="rv2-pdf-panel">
            <div className="rv2-pdf-panel-title">Seções a incluir no PDF</div>
            <div className="rv2-pdf-panel-grid">
              {secoesPdf.map((s) => (
                <label key={s.key} className="rv2-pdf-panel-item">
                  <input
                    type="checkbox"
                    checked={pdfSelection[s.key]}
                    onChange={() => togglePdfSection(s.key)}
                  />
                  <span>{s.label}</span>
                </label>
              ))}
            </div>
            <div className="rv2-pdf-panel-actions">
              <button
                className="rv2-btn-secondary"
                onClick={() => setPdfSelection((prev) => secoesPdf.reduce((a, s) => ({ ...a, [s.key]: true }), { ...prev }))}
              >Marcar todas</button>
              <button
                className="rv2-btn-secondary"
                onClick={() => setPdfSelection((prev) => secoesPdf.reduce((a, s) => ({ ...a, [s.key]: false }), { ...prev }))}
              >Desmarcar todas</button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="rv2-content" id="report-content">

          {/* Descrição da Não Conformidade */}
          {dados_basicos.descricao_nc && (
            <div className="rv2-card rv2-full-card" data-pdf-section="descricao" style={{ background: '#ffffff', borderLeft: '4px solid #006948' }}>
              <span className="rv2-card-badge" style={{ background: '#006948', color: '#ffffff' }}>Descrição da Não Conformidade</span>
              <p style={{
                margin: 0,
                lineHeight: '1.7',
                color: '#111c2d',
                fontSize: '14px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                <MarkdownText text={dados_basicos.descricao_nc} />
              </p>
            </div>
          )}

          {/* Cliente Emissor (origem externa — Feature 1) */}
          {reportData.origem?.origem_tipo === 'cliente' && (
            <div className="rv2-card rv2-full-card" data-pdf-section="cliente_emissor" style={{ background: '#eff6ff', borderLeft: '4px solid #1d4ed8' }}>
              <span className="rv2-card-badge" style={{ background: '#1d4ed8', color: '#fff' }}><span className="material-symbols-outlined rv2-badge-icon">apartment</span>Origem externa — emitida pelo cliente</span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginTop: '12px' }}>
                {reportData.origem.cliente_emissor && (
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#57657a', marginBottom: '4px' }}>Cliente emissor</div>
                    <div style={{ fontSize: '14px', color: '#111c2d', fontWeight: 600 }}>{reportData.origem.cliente_emissor}</div>
                  </div>
                )}
                {reportData.origem.numero_nc_cliente && (
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#57657a', marginBottom: '4px' }}>Nº NC no cliente</div>
                    <div style={{ fontSize: '14px', color: '#111c2d', fontWeight: 600 }}>{reportData.origem.numero_nc_cliente}</div>
                  </div>
                )}
                {reportData.origem.prazo_resposta_cliente && (
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#57657a', marginBottom: '4px' }}>Prazo de resposta</div>
                    <div style={{ fontSize: '14px', color: '#111c2d', fontWeight: 600 }}>{reportData.origem.prazo_resposta_cliente}</div>
                  </div>
                )}
                {reportData.origem.contato_cliente_email && (
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#57657a', marginBottom: '4px' }}>Contato</div>
                    <div style={{ fontSize: '14px', color: '#111c2d', fontWeight: 600 }}>{reportData.origem.contato_cliente_email}</div>
                  </div>
                )}
                {reportData.origem.arquivo_original_nome && (
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#57657a', marginBottom: '4px' }}>Arquivo original</div>
                    <div style={{ fontSize: '14px', color: '#111c2d', fontWeight: 600 }}>{reportData.origem.arquivo_original_nome}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Bento grid: Score + Dados Básicos | Análise */}
          <div className="rv2-bento">

            {/* Coluna esquerda */}
            <div className="rv2-col-left">

              {/* Score card — branding do cliente SEMPRE; a nota (score) só no modo Investigação (Registro não recebe nota, Sessão 40) */}
              <div className="rv2-card rv2-score-card" data-pdf-section="score">
                {reportData.analise_v4?.modo_analise !== 'registro' && (
                  <span className="rv2-card-badge">Completude da análise</span>
                )}
                {/* Logo e dados do cliente acima do score */}
                {(clientLogoSrc || empresa.razao_social) && (
                  <div className="rv2-empresa-info">
                    {clientLogoSrc && (
                      <img
                        src={clientLogoSrc}
                        alt={empresaNome}
                        className="rv2-empresa-logo"
                      />
                    )}
                    <div className="rv2-empresa-dados">
                      {empresa.razao_social && <strong>{empresa.nome_fantasia || empresa.razao_social}</strong>}
                      {/* #314 — CNPJ do tenant logo abaixo do nome (deterrente anti-uso-de-CNPJ-de-terceiro) */}
                      {empresa.cnpj && <span>CNPJ: {formatCNPJ(empresa.cnpj)}</span>}
                      {empresa.telefone && <span>{empresa.telefone}</span>}
                      {empresa.website && <span>{empresa.website}</span>}
                    </div>
                  </div>
                )}
                {reportData.analise_v4?.modo_analise !== 'registro' && (<>
                <div className="rv2-score-chart-wrap">
                  <div className="rv2-score-donut">
                    <Doughnut data={scoreChartData} options={scoreChartOptions} />
                    <div className="rv2-score-center">
                      <span className="rv2-score-number" style={{ color: scoreColor }}>{score}</span>
                      <span className="rv2-score-sub">
                        {score >= 80 ? 'Completa' : score >= 60 ? 'Boa' : score >= 40 ? 'Parcial' : 'Incompleta'}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="rv2-score-clarifier" style={{ fontSize: '11px', lineHeight: 1.4, color: '#57657a', margin: '10px 0 0', textAlign: 'center' }}>
                  Mede o quão completa e estruturada está a análise; não garante a correção da causa raiz.
                </p>
                </>)}
              </div>

              {/* Dados básicos */}
              <div className="rv2-card rv2-dados-card">
                <span className="rv2-card-badge">Dados Básicos</span>
                <div className="rv2-dados-list">
                  {[
                    // #264 — em setor de SERVIÇO, Produto/Lote só aparecem se
                    // tiverem valor real (o backend sinaliza via is_servico).
                    // Num serviço sem produto/lote, omite — não há físico a
                    // identificar; "Não identificado" soava estranho. Setor
                    // industrial: inalterado (mostra placeholder como antes).
                    // #264b — quando PREENCHIDOS num serviço, renomeia os rótulos
                    // (Serviço / Processo · Período / Referência), strings de setor.js.
                    [labelsFisicos(dados_basicos.is_servico).produto, dados_basicos.is_servico && isPlaceholderFisico(dados_basicos.produto) ? null : dados_basicos.produto],
                    [labelsFisicos(dados_basicos.is_servico).lote, dados_basicos.is_servico && isPlaceholderFisico(dados_basicos.lote) ? null : dados_basicos.lote],
                    ['Status', statusLabel[dados_basicos.workflow_status] || dados_basicos.workflow_status],
                    ['Versão', `v${dados_basicos.versao_analise}`],
                    ['Origem', dados_basicos.origem],
                    // #51 (Sessão 32): custo só aparece no documento quando preenchido
                    ['Custo estimado', dados_basicos.custo_estimado != null ? `R$ ${formatCurrencyBR(dados_basicos.custo_estimado)}` : null],
                    // #279/#280 — Tratamento da saída não conforme (ISO 8.7) + correção × ação corretiva + abrangência (omite quando vazio)
                    ['Disposição da saída NC', labelDisposicao(dados_basicos.disposicao_saida)],
                    ['Requer ação corretiva', labelSimNao(dados_basicos.requer_acao_corretiva)],
                    ['Abrangência verificada', labelSimNao(dados_basicos.abrangencia_verificada)],
                    // #283 / #158 — Alterações no SGQ + reavaliação de riscos (os textos só quando 'sim'; omitidos vazios)
                    ['Alteração no SGQ', labelSimNao(dados_basicos.requer_alteracao_sgq)],
                    ['Documentos SGQ alterados', dados_basicos.requer_alteracao_sgq === 'sim' ? dados_basicos.documentos_sgq_alterados : null],
                    ['Reavaliação de riscos', labelSimNao(dados_basicos.requer_reavaliacao_riscos)],
                    ['Reavaliação de riscos/oportunidades', dados_basicos.requer_reavaliacao_riscos === 'sim' ? dados_basicos.reavaliacao_riscos_oportunidades : null],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} className="rv2-dados-row">
                      <span className="rv2-dados-label">{label}</span>
                      <span className="rv2-dados-value">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Coluna direita: Análise dos Porquês */}
            <div className="rv2-col-right">
              <div className="rv2-card rv2-porques-card" data-pdf-section="porques">
                <span className="rv2-card-badge">Análise dos Porquês</span>
                <div className="rv2-porques-list">
                  {(analise.cinco_porques || [])
                    .map(p => (typeof p === 'object' && p !== null)
                      ? (p.resposta || p.pergunta || '')
                      : (p || ''))
                    .filter(s => s && String(s).trim())
                    .map((p, i, arr) => (
                    <div key={i} className={`rv2-porque-item rv2-porque-${i}`}>
                      <div className={`rv2-porque-num ${i === arr.length - 1 ? 'last' : ''}`}>{i + 1}</div>
                      <div className="rv2-porque-text">
                        <span className="rv2-porque-label">
                          {i === 0 ? 'O Problema' : i === arr.length - 1 ? 'Causa Raiz' : `Por quê ${i + 1}?`}
                        </span>
                        <p className={i === arr.length - 1 ? 'rv2-causa-raiz' : ''}>{p}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {analise.causa_raiz && (
                  <div className="rv2-causa-box">
                    <span className="rv2-dados-label">Causa Raiz Identificada</span>
                    <p><MarkdownText text={analise.causa_raiz} /></p>
                    {/* Badge de confiança/hipótese REMOVIDO do relatório cliente-facing
                        (decisão Francis): o relatório é exportado pela própria equipe de
                        qualidade (que assina a análise); e pra NC de fornecedor a confiança
                        é FORÇADA pra Média/Baixa (constante metodológica, não medida de
                        qualidade), então o rótulo "hipótese a confirmar" passava ao cliente
                        a falsa impressão de trabalho especulativo apesar da análise +
                        validação da equipe. A confiança/validação seguem sinalizadas
                        INTERNAMENTE (tela de análise: badge "Confiança" + justificativa;
                        flag requer_validacao no JSON). */}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* v4.0: Análise 6M (Ishikawa) */}
          {reportData.analise_v4?.compreensao?.analise_6m && (
            <div className="rv2-card rv2-full-card" data-pdf-section="analise_6m">
              <span className="rv2-card-badge" style={{ background: '#006948', color: '#fff' }}>Análise 6M (Ishikawa)</span>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginTop: '12px' }}>
                {[
                  ['👷 Mão de obra', reportData.analise_v4.compreensao.analise_6m.mao_de_obra],
                  ['📋 Método', reportData.analise_v4.compreensao.analise_6m.metodo],
                  ['⚙️ Máquina', reportData.analise_v4.compreensao.analise_6m.maquina],
                  ['🧱 Material', reportData.analise_v4.compreensao.analise_6m.material],
                  ['📏 Medição', reportData.analise_v4.compreensao.analise_6m.medicao],
                  ['🌡️ Meio ambiente', reportData.analise_v4.compreensao.analise_6m.meio_ambiente],
                ].filter(([, value]) => ramo6MTemConteudo(value)).map(([label, value]) => {
                  const isPrincipal = reportData.analise_v4.compreensao.analise_6m.pilar_principal &&
                    label.toLowerCase().includes(reportData.analise_v4.compreensao.analise_6m.pilar_principal.toLowerCase().split(' ')[0]);
                  return (
                    <div key={label} style={{
                      padding: '12px', borderRadius: '8px',
                      border: isPrincipal ? '2px solid #006948' : '1px solid #e7eeff',
                      background: isPrincipal ? '#f0f9f5' : '#f0f3ff',
                      position: 'relative'
                    }}>
                      <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#57657a', marginBottom: '4px' }}>{label}</div>
                      <div style={{ fontSize: '13px', color: '#111c2d', lineHeight: 1.5 }}><MarkdownText text={value} /></div>
                      {isPrincipal && <div style={{ fontSize: '9px', fontWeight: 700, color: '#006948', textTransform: 'uppercase', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '3px' }}><span className="material-symbols-outlined" style={{ fontSize: '12px', lineHeight: 1 }}>arrow_upward</span>Pilar principal</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* v4.0: Hipótese Alternativa — TD49: só renderiza quando IA gerou
              hipótese real (declaracao populada). Antes renderizava com
              mensagem "não foi gerada nesta análise" no caminho else, o que
              parecia falha técnica em modal/PDF cliente-facing. */}
          {reportData.analise_v4?.hipotese_alternativa?.declaracao && (
            <div className="rv2-card rv2-full-card" data-pdf-section="hipotese_alternativa" style={{ background: '#fffbeb', borderLeft: '4px solid #f59e0b' }}>
              <span className="rv2-card-badge" style={{ background: '#f59e0b', color: '#fff' }}>Hipótese Alternativa</span>
              <p style={{ fontSize: '14px', fontWeight: 600, color: '#92400e', margin: '0 0 10px' }}>
                <MarkdownText text={reportData.analise_v4.hipotese_alternativa.declaracao} />
              </p>
              {reportData.analise_v4.hipotese_alternativa.porques?.length > 0 && (
                <ol style={{ margin: '6px 0 10px', paddingLeft: '20px', fontSize: '13px', color: '#44403c' }}>
                  {reportData.analise_v4.hipotese_alternativa.porques.map((p, i) => (
                    <li key={i} style={{ marginBottom: '4px' }}>
                      {formatHipoteseAlternativaPorque(p)}
                    </li>
                  ))}
                </ol>
              )}
              {reportData.analise_v4.hipotese_alternativa.por_que_nao_selecionada && (
                <p style={{ fontSize: '13px', color: '#78716c', margin: 0, paddingTop: '10px', borderTop: '1px solid #fde68a' }}>
                  <strong>Por que não selecionada:</strong> <MarkdownText text={reportData.analise_v4.hipotese_alternativa.por_que_nao_selecionada} />
                </p>
              )}
            </div>
          )}

          {/* v4.0: Ação de Contenção Imediata (modo Investigação) */}
          {reportData.analise_v4?.acao_contencao_imediata?.descricao && (
            <div className="rv2-card rv2-full-card" data-pdf-section="contencao" style={{ background: '#fef2f2', borderLeft: '4px solid #dc2626' }}>
              <span className="rv2-card-badge" style={{ background: '#dc2626', color: '#fff' }}><span className="material-symbols-outlined rv2-badge-icon">e911_emergency</span>Ação de Contenção Imediata</span>
              <p style={{ fontSize: '14px', color: '#0d1b2a', margin: '0 0 10px', lineHeight: 1.6 }}>
                <MarkdownText text={reportData.analise_v4.acao_contencao_imediata.descricao} />
              </p>
              <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#57657a' }}>
                <span><span className="material-symbols-outlined rv2-inline-icon">schedule</span>Prazo: {reportData.analise_v4.acao_contencao_imediata.prazo_horas}h</span>
                <span><span className="material-symbols-outlined rv2-inline-icon">person</span>{reportData.analise_v4.acao_contencao_imediata.responsavel_funcao}</span>
              </div>
            </div>
          )}

          {/*
            Ação Imediata (modo Registro) — FIX(I4) + Tier 1 Bug 2
            simétrico modal (Sessão 14 Briefing A-bis).

            Mutual exclusion adicionada: seção só renderiza quando NÃO há
            v4 canônico (`!reportData.analise_v4?.acao_contencao_imediata?.descricao`).

            Causa raiz cumulativa:
              - Backend `_map_v4_to_legacy:265-267` (ai_service.py) copia
                `analise_v4.acao_contencao_imediata.descricao` →
                `legacy.acao_imediata` por retrocompat. NC v4 moderna
                tem AMBOS os campos populados com mesma string.
              - Frontend pré-fix renderizava tanto a tag vermelha
                ("🚨 Ação de Contenção Imediata") quanto a laranja
                ("⏱ Ação Imediata") com texto verbatim idêntico.

            Empírico em RNC-2026-0003 (Sessão 14): modal mostrava ambas
            as tags com mesmo texto. Briefing A fixou o PDF
            (`ReportPrintable.jsx` commit ae300ac); este commit fecha
            o modal + commit irmão fecha NCResultAnalysisSection.

            Lição 61 viva (3ª instância confirmada): fix em camada A
            (PDF) precisava de TD-followup explícito pras camadas B
            (modal) e C (tela inline) antes de fechar TD original.
          */}
          {analise.acao_imediata
            && !reportData.analise_v4?.acao_contencao_imediata?.descricao && (
            <div className="rv2-card rv2-full-card" data-pdf-section="acao_imediata" style={{ background: '#fffbeb', borderLeft: '4px solid #f59e0b' }}>
              <span className="rv2-card-badge" style={{ background: '#f59e0b', color: '#fff' }}><span className="material-symbols-outlined rv2-badge-icon">schedule</span>Ação Imediata</span>
              <p style={{ fontSize: '14px', color: '#0d1b2a', margin: '0', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                <MarkdownText text={analise.acao_imediata} />
              </p>
            </div>
          )}

          {/* Causa Raiz (declaração explícita) — FIX(I4) */}
          {analise.causa_raiz && (
            <div className="rv2-card rv2-full-card" data-pdf-section="causa_raiz" style={{ background: '#f0fdf4', borderLeft: '4px solid #006948' }}>
              <span className="rv2-card-badge" style={{ background: '#006948', color: '#fff' }}><span className="material-symbols-outlined rv2-badge-icon">target</span>Causa Raiz</span>
              <p style={{ fontSize: '14px', color: '#0d1b2a', margin: '0', lineHeight: 1.6, fontWeight: 500 }}>
                <MarkdownText text={analise.causa_raiz} />
              </p>
            </div>
          )}

          {/* Ações Corretivas */}
          {analise.acoes_corretivas && analise.acoes_corretivas.length > 0 && (
            <div className="rv2-card rv2-full-card" data-pdf-section="acoes">
              <span className="rv2-card-badge">Ações Corretivas</span>
              <div className="rv2-acoes-grid">
                {analise.acoes_corretivas.map((acao, i) => {
                  const prioridade = acao.prioridade || 'media';
                  const prioColors = {
                    critica: '#f44336',
                    alta: '#ff9800',
                    media: '#2196f3',
                    baixa: '#4caf50'
                  };
                  const prioColor = prioColors[prioridade.toLowerCase()] || '#2196f3';
                  
                  return (
                    <div key={i} className="rv2-acao-item" style={{ borderLeft: `4px solid ${prioColor}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: '#57657a' }}>
                          Ação {i + 1}
                        </span>
                        <span style={{ 
                          display: 'inline-block', 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          background: prioColor, 
                          color: 'white', 
                          fontSize: '9px', 
                          fontWeight: '700',
                          textTransform: 'uppercase'
                        }}>
                          {prioridade}
                        </span>
                      </div>
                      <p className="rv2-acao-text"><MarkdownText text={acao.what || acao.acao || acao.descricao} /></p>
                      {(acao.who || acao.responsavel) && (
                        <span className="rv2-acao-resp"><span className="material-symbols-outlined rv2-inline-icon">person</span>{acao.who || acao.responsavel}</span>
                      )}
                      {acao.when && (
                        <span className="rv2-acao-resp" style={{ marginLeft: '12px' }}><span className="material-symbols-outlined rv2-inline-icon">event</span>{acao.when}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Ações Preventivas (v4 — modo Investigação) */}
          {reportData.analise_v4?.acoes_preventivas?.length > 0 && (
            <div className="rv2-card rv2-full-card" data-pdf-section="acoes_preventivas">
              <span className="rv2-card-badge" style={{ background: '#10b981', color: '#fff' }}><span className="material-symbols-outlined rv2-badge-icon">shield</span>Ações Preventivas</span>
              <div className="rv2-acoes-grid">
                {reportData.analise_v4.acoes_preventivas.map((acao, i) => {
                  const prioridade = (acao.prioridade || 'media').toLowerCase();
                  const prioColors = { critica: '#f44336', alta: '#ff9800', media: '#2196f3', baixa: '#4caf50' };
                  const prioColor = prioColors[prioridade] || '#10b981';
                  return (
                    <div key={i} className="rv2-acao-item" style={{ borderLeft: `4px solid ${prioColor}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#57657a' }}>
                          Preventiva {i + 1}
                        </span>
                        {acao.prioridade && (
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '12px', background: prioColor, color: 'white', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase' }}>
                            {prioridade}
                          </span>
                        )}
                      </div>
                      <p className="rv2-acao-text"><MarkdownText text={acao.acao || acao.descricao || acao.what} /></p>
                      {acao.responsavel && <span className="rv2-acao-resp"><span className="material-symbols-outlined rv2-inline-icon">person</span>{acao.responsavel}</span>}
                      {acao.responsavel_funcao && !acao.responsavel && <span className="rv2-acao-resp"><span className="material-symbols-outlined rv2-inline-icon">person</span>{acao.responsavel_funcao}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Impacto Potencial (v2.4.0) */}
          {analise.impacto_potencial && (
            <div className="rv2-card rv2-full-card" data-pdf-section="impacto" style={{ background: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
              <span className="rv2-card-badge" style={{ background: '#ff9800' }}>Impacto Potencial</span>
              <div className="rv2-dados-list">
                {[
                  ['📊 Cliente', analise.impacto_potencial.cliente],
                  ['⚙️ Operacional', analise.impacto_potencial.operacional],
                ].filter(([, v]) => v).map(([label, value]) => (
                  <div key={label} className="rv2-dados-row">
                    <span className="rv2-dados-label" style={{ color: '#e65100' }}>{label}</span>
                    <span className="rv2-dados-value"><MarkdownText text={value} /></span>
                  </div>
                ))}
                {analise.impacto_potencial.financeiro && (
                  <div className="rv2-dados-row">
                    <span className="rv2-dados-label" style={{ color: '#e65100' }}>💰 Financeiro</span>
                    {/*
                      TD85: badge colorido removido. IA v4 retorna `financeiro_estimado`
                      como string LIVRE (ex: "R$ 50k em retrabalho", "Significativo"),
                      não enum 'baixo|medio|alto'. Ternário `=== 'alto'` nunca casava
                      pra NCs v4 → badge sempre verde → UI mentia sobre criticidade.
                      Fix opção (b) cravada: mostrar texto literal, sem semaforização
                      inventada. Decisão arquitetural sobre semaforização rica fica
                      pra sprint dedicada (backend normalizar p/ enum OU campos
                      separados financeiro_categoria + financeiro_descricao).
                    */}
                    <span className="rv2-dados-value">
                      <MarkdownText text={analise.impacto_potencial.financeiro} />
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Risco de Recorrência (v2.4.0) */}
          {analise.risco_recorrencia && (
            <div className="rv2-card rv2-full-card" data-pdf-section="risco" style={{ background: '#ffebee', borderLeft: '4px solid #f44336' }}>
              <span className="rv2-card-badge" style={{ background: '#f44336' }}>Risco de Recorrência</span>
              <div className="rv2-dados-list">
                <div className="rv2-dados-row">
                  <span className="rv2-dados-label" style={{ color: '#c62828' }}>Nível de Risco</span>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    background: analise.risco_recorrencia.nivel === 'alto' ? '#f44336' :
                               analise.risco_recorrencia.nivel === 'medio' ? '#ff9800' :
                               analise.risco_recorrencia.nivel === 'indeterminado' ? '#9CA3AF' : '#4caf50',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: '700',
                    textTransform: 'uppercase'
                  }}>
                    {analise.risco_recorrencia.nivel}
                  </span>
                </div>
                {analise.risco_recorrencia.justificativa && (
                  <div className="rv2-dados-row">
                    <span className="rv2-dados-label" style={{ color: '#c62828' }}>Justificativa</span>
                    <span className="rv2-dados-value"><MarkdownText text={analise.risco_recorrencia.justificativa} /></span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Conclusão Executiva (v2.4.0) */}
          {analise.conclusao_executiva && (
            <div className="rv2-card rv2-full-card" data-pdf-section="conclusao" style={{ background: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
              <span className="rv2-card-badge" style={{ background: '#4caf50' }}>Conclusão Executiva</span>
              <p style={{ margin: 0, lineHeight: '1.8', color: '#111c2d', fontSize: '14px' }}>
                <MarkdownText text={analise.conclusao_executiva} />
              </p>
            </div>
          )}

          {/* Plano 5W2H — backend (report_service_v2) emite uma LISTA com uma entrada por ação corretiva.
              Sessão 41: tier Registro não exibe 5W2H (oculto; perk da Investigação). */}
          {reportData.analise_v4?.modo_analise !== 'registro' && Array.isArray(reportData.plano_5w2h) && reportData.plano_5w2h.length > 0 && (
            <div className="rv2-card rv2-full-card rv2-5w2h-card">
              <div className="rv2-5w2h-header">
                <span className="rv2-dados-label">Plano de Ação Corretiva</span>
                <h3>Estratégia 5W2H</h3>
              </div>
              {reportData.plano_5w2h.map((acao, idx) => (
                <div key={idx} className="rv2-5w2h-grid" style={{ marginBottom: idx < reportData.plano_5w2h.length - 1 ? '20px' : 0, paddingBottom: idx < reportData.plano_5w2h.length - 1 ? '16px' : 0, borderBottom: idx < reportData.plano_5w2h.length - 1 ? '1px solid #e7eeff' : 'none' }}>
                  {[
                    ['WHAT (O QUÊ)', acao.what],
                    ['WHY (POR QUÊ)', acao.why],
                    ['WHERE (ONDE)', acao.where],
                    ['WHEN (QUANDO)', acao.when],
                    ['WHO (QUEM)', acao.who],
                    ['HOW (COMO)', acao.how],
                    ['HOW MUCH (QUANTO)', acao.how_much],
                  ].map(([label, value]) => (
                    <div key={label} className="rv2-5w2h-item">
                      <span className="rv2-5w2h-label">{label}</span>
                      <p className="rv2-5w2h-value">{value || 'Não definido'}</p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Gantt Expandido (v2.4.0) — Sessão 41: tier Registro não exibe cronograma. */}
          {reportData.analise_v4?.modo_analise !== 'registro' && reportData.gantt_data && reportData.gantt_data.length > 0 && (
            <div className="rv2-card rv2-full-card" data-pdf-section="gantt">
              <span className="rv2-card-badge">Cronograma de Implementação (Gantt)</span>
              <div className="rv2-gantt-wrap">
                <table className="rv2-gantt-table">
                  <thead>
                    <tr>
                      {['Tarefa', 'Responsável', 'Data Início', 'Data Fim', 'Investimento'].map(h => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(reportData.gantt_data) ? reportData.gantt_data : []).map((task, i) => {
                      const critica = task.critica || false;
                      
                      return (
                        <tr key={i} style={{ background: critica ? '#fff3e0' : 'transparent' }}>
                          <td>
                            {critica && <span className="material-symbols-outlined rv2-inline-icon" style={{ color: '#f44336', fontSize: '12px' }}>circle</span>}
                            {task.name || task.tarefa || 'N/A'}
                          </td>
                          <td>{task.owner || task.responsavel || 'N/A'}</td>
                          <td>{task.start || task.inicio || 'N/A'}</td>
                          <td>{task.end || task.fim || 'N/A'}</td>
                          <td className="rv2-gantt-cost">{task.cost || task.custo || 'N/A'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Validação e Status (v2.4.0) */}
          {analise.validacao_status && (
            <div className="rv2-card rv2-full-card" data-pdf-section="validacao" style={{ background: '#e3f2fd', borderLeft: '4px solid #2196f3' }}>
              <span className="rv2-card-badge" style={{ background: '#2196f3' }}>Validação e Status</span>
              <div className="rv2-dados-list">
                <div className="rv2-dados-row">
                  <span className="rv2-dados-label" style={{ color: '#1565c0' }}>Status da NC</span>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    background: analise.validacao_status.status_nc === 'concluida' ? '#4caf50' :
                               analise.validacao_status.status_nc === 'em_execucao' ? '#9c27b0' :
                               analise.validacao_status.status_nc === 'em_analise' ? '#ff9800' : '#2196f3',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: '700',
                    textTransform: 'uppercase'
                  }}>
                    {(analise.validacao_status.status_nc || 'nova').replace('_', ' ')}
                  </span>
                </div>
                <div style={{ marginTop: '16px', marginBottom: '8px' }}>
                  <span className="rv2-dados-label" style={{ color: '#1565c0' }}>Checklist de Validação</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '18px', lineHeight: 1, color: analise.validacao_status.revisado_equipe ? '#006948' : '#9CA3AF' }}>{analise.validacao_status.revisado_equipe ? 'check_box' : 'check_box_outline_blank'}</span>
                    <span>Revisado pela equipe</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '18px', lineHeight: 1, color: analise.validacao_status.ajustes_realizados ? '#006948' : '#9CA3AF' }}>{analise.validacao_status.ajustes_realizados ? 'check_box' : 'check_box_outline_blank'}</span>
                    <span>Ajustes realizados</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '18px', lineHeight: 1, color: analise.validacao_status.aprovado_execucao ? '#006948' : '#9CA3AF' }}>{analise.validacao_status.aprovado_execucao ? 'check_box' : 'check_box_outline_blank'}</span>
                    <span>Aprovado para execução</span>
                  </div>
                </div>
                {analise.validacao_status.observacoes && (
                  <div style={{ marginTop: '12px', padding: '12px', background: '#fff', borderRadius: '6px' }}>
                    <strong>Observações:</strong><br />
                    {analise.validacao_status.observacoes}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recorrência */}
          {recorrencia?.detectado && (
            <div className="rv2-card rv2-full-card rv2-recorrencia-card">
              <span className="rv2-card-badge">Recorrência Detectada</span>
              <p><strong>Casos Similares:</strong> {recorrencia.casos_similares}</p>
              <p><strong>Padrão:</strong> <MarkdownText text={recorrencia.padrao_identificado} /></p>
              {recorrencia.recomendacao && <p><strong>Recomendação:</strong> <MarkdownText text={recorrencia.recomendacao} /></p>}
            </div>
          )}

          {/* Avaliação de Eficácia */}
          {reportData.eficacia?.eficacia_avaliada && (
            <div className="rv2-card rv2-full-card">
              <span className="rv2-card-badge">Avaliação de Eficácia</span>
              <div className="rv2-eficacia-content">
                <span className={`rv2-eficacia-badge rv2-eficacia-${reportData.eficacia.eficacia_avaliada}`}>
                  {reportData.eficacia.eficacia_avaliada.replace(/_/g, ' ').toUpperCase()}
                </span>
                <div className="rv2-dados-list" style={{ marginTop: '16px' }}>
                  {[
                    ['Responsável', reportData.eficacia.responsavel_avaliacao],
                    ['Data da Avaliação', reportData.eficacia.data_avaliacao_eficacia
                      ? new Date(reportData.eficacia.data_avaliacao_eficacia + 'T00:00:00').toLocaleDateString('pt-BR')
                      : 'Não realizada'],
                    ['Justificativa', reportData.eficacia.justificativa_eficacia],
                    ['Alteração SGQ', reportData.eficacia.requer_alteracao_sgq?.toUpperCase()],
                    ['Reavaliação de riscos', reportData.eficacia.requer_reavaliacao_riscos?.toUpperCase()],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} className="rv2-dados-row">
                      <span className="rv2-dados-label">{label}</span>
                      <span className="rv2-dados-value">{value}</span>
                    </div>
                  ))}
                </div>
                {reportData.eficacia.requer_alteracao_sgq === 'sim' && reportData.eficacia.documentos_sgq_alterados && (
                  <div className="rv2-sgq-box">
                    <span className="rv2-dados-label">Documentos SGQ Alterados</span>
                    <p>{reportData.eficacia.documentos_sgq_alterados}</p>
                  </div>
                )}
                {reportData.eficacia.requer_reavaliacao_riscos === 'sim' && reportData.eficacia.reavaliacao_riscos_oportunidades && (
                  <div className="rv2-sgq-box">
                    <span className="rv2-dados-label">Reavaliação de Riscos e Oportunidades</span>
                    <p>{reportData.eficacia.reavaliacao_riscos_oportunidades}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Evidências Anexadas */}
          {reportData.arquivos && reportData.arquivos.length > 0 && (
            <div className="rv2-card rv2-full-card">
              <span className="rv2-card-badge">Evidências Anexadas</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                {reportData.arquivos.map((arq, i) => {
                  const isImage = arq.filename?.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i);
                  // FIX(G2): preferir data_url (persistido no banco, sobrevive a
                  // redeploys do Railway) e cair para filepath estático apenas
                  // como fallback para NCs antigas.
                  const fileUrl = arq.data_url
                    || (arq.filepath ? `${API_URL}/${arq.filepath.replace(/^\.?\/?/, '')}` : null);

                  return (
                    <div key={i} style={{ 
                      display: 'flex', flexDirection: 'column', gap: '8px',
                      padding: '12px', background: '#f0f3ff', borderRadius: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '1.5rem', lineHeight: 1, color: '#57657a' }}>
                          {isImage ? 'image' :
                           arq.filename?.match(/\.(pdf)$/i) ? 'picture_as_pdf' :
                           arq.filename?.match(/\.(xls|xlsx|csv)$/i) ? 'table_chart' :
                           arq.filename?.match(/\.(doc|docx)$/i) ? 'description' : 'attach_file'}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#111c2d' }}>{arq.filename}</div>
                          {arq.uploaded_at && (
                            <div style={{ fontSize: '0.75rem', color: '#57657a' }}>
                              Anexado em: {new Date(arq.uploaded_at).toLocaleDateString('pt-BR')}
                            </div>
                          )}
                        </div>
                      </div>
                      {isImage && fileUrl && (
                        <img
                          src={fileUrl}
                          alt={arq.filename}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '400px',
                            borderRadius: '8px',
                            objectFit: 'contain',
                            marginTop: '4px',
                            border: '1px solid #e7eeff'
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            const msg = arq.has_blob
                              ? '⚠️ Erro ao decodificar imagem.'
                              : '⚠️ Arquivo não disponível neste deploy. Reanexe a evidência se precisar incluí-la no relatório.';
                            e.target.parentElement.insertAdjacentHTML('beforeend',
                              `<div style="padding:12px;color:#93000a;font-size:0.75rem;">${msg}</div>`
                            );
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="rv2-footer">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontWeight: 700, color: '#006948' }}>www.qaasa.com.br</span>
              <span style={{ fontSize: '10px' }}>QUALITY AS A SERVICE, POWERED BY AI</span>
            </div>
            {/* Decisão 2 Sessão 22: timezone do tenant via formatDateTime.
                Util lê automaticamente do localStorage.user.client_timezone
                quando o segundo argumento é null (ReportV2 não recebe
                user como prop). Fallback final é 'America/Sao_Paulo'. */}
            <span>Gerado em: {formatDateTime(reportData.metadata?.gerado_em || Date.now())}</span>
          </div>

        </div>
      </div>
    </div>
  );
}

export default ReportV2;
